import { config } from "../../shared/config/environment";
import { fetchWithRefresh, type RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { ConnectionAuthConfig, ConnectionWithAuth } from "../auth/types";
import { getConnectionAuthConfig } from "./connection-auth-config";
import type {
  OutlookIntegrationSettings,
  StudioModule,
  StudioModuleConnector,
  StudioSettingsData,
  StudioSettingsResponse,
  StudioSettingsWireData,
  StudioSettingsWireResponse,
  StudioWireConnection,
} from "./types";

export class StudioUnreachableError extends Error {
  public readonly originalError: unknown;
  constructor(originalError?: unknown) {
    super("Studio settings unreachable");
    this.name = "StudioUnreachableError";
    this.originalError = originalError;
  }
}

export class StudioBadResponseError extends Error {
  constructor(public readonly status: number) {
    super(`Studio returned HTTP ${status}`);
    this.name = "StudioBadResponseError";
  }
}

export interface FetchSettingsArgs {
  channel: string;
  bearer?: string;
}

/* ── SSO discovery (pre-auth MSAL bootstrap, AIVA-6162) ──────────────── */

/**
 * Non-secret MSAL bootstrap config served by
 * GET /api/{channel}/sso-config (nonai-addin-api SsoConfigController).
 * `scopes` is null when the backend doesn't centrally manage them, in which
 * case the caller falls back to its own defaults.
 */
export interface SsoDiscoveryConfig {
  provider: string;
  protocol: string;
  authority: string;
  clientId: string;
  scopes: string[] | null;
  /** OIDC discovery (`.well-known/openid-configuration`) URL, when stored. Used
   *  by the generic OIDC client for non-Azure providers to run metadata discovery. */
  discoveryUrl: string | null;
  /** Minutes of no task-pane use before sign-out (Session Persistence on the
   *  Studio SSO page), or null when the workspace has not set one. */
  idleTimeoutMinutes: number | null;
}

interface SsoDiscoveryWireResponse {
  success?: boolean;
  data?: {
    provider?: string;
    protocol?: string;
    authority?: string;
    clientId?: string;
    scopes?: string[] | null;
    discoveryUrl?: string | null;
    idleTimeoutMinutes?: number | null;
  } | null;
  error?: string | null;
  correlationId?: string | null;
}

/**
 * Fetches the SSO discovery config used to bootstrap MSAL. This runs
 * **before** the user has authenticated, so it sends no bearer — the route is
 * anonymous at APIM. On any failure the caller decides whether to fall back to
 * env config (dev) or block sign-in (prod), so this throws rather than
 * returning a partial result:
 *  - {@link StudioUnreachableError} when the base URL is missing or the fetch
 *    throws (network / DNS / CORS),
 *  - {@link StudioBadResponseError} on a non-2xx status or a 2xx payload that
 *    lacks the mandatory clientId / authority.
 */
export async function fetchSsoDiscovery(
  channel: string,
  opts: { fetchImpl?: typeof fetch } = {},
): Promise<SsoDiscoveryConfig> {
  if (!config.addinApiBaseUrl) {
    throw new StudioUnreachableError("Addin API base URL not configured");
  }

  const url = `${config.addinApiBaseUrl}/api/${encodeURIComponent(channel)}/sso-config`;

  let response: Response;
  try {
    // A plain, unauthenticated GET — no bearer, no refresh callback, no access
    // re-validation (all of which assume a signed-in user we don't have yet).
    response = await (opts.fetchImpl ?? fetch)(url, { headers: buildHeaders() });
  } catch (err) {
    throw new StudioUnreachableError(err);
  }

  if (!response.ok) {
    throw new StudioBadResponseError(response.status);
  }

  const payload = (await response.json()) as SsoDiscoveryWireResponse;
  const data = payload.data;
  if (!data?.clientId || !data?.authority) {
    // A 200 with an incomplete body is unusable for MSAL — treat it like a bad
    // response so the caller applies its fallback/blocking path.
    throw new StudioBadResponseError(response.status);
  }

  return {
    provider: data.provider ?? "",
    protocol: data.protocol ?? "",
    authority: data.authority,
    clientId: data.clientId,
    scopes: data.scopes ?? null,
    discoveryUrl: data.discoveryUrl ?? null,
    // Only a whole number of minutes survives; anything else leaves the auth
    // layer on its built-in default rather than running an unusable window.
    idleTimeoutMinutes:
      typeof data.idleTimeoutMinutes === "number" && Number.isInteger(data.idleTimeoutMinutes)
        ? data.idleTimeoutMinutes
        : null,
  };
}

interface FetchSettingsOptions {
  fetchImpl?: typeof fetch;
  /**
   * Re-acquires a fresh backend token on a 401 so the request retries once with
   * it instead of surfacing the raw 401 to the caller. Supplied by callers that
   * have access to AuthState's `refreshTokens`; omitted in tests.
   */
  refresh?: RefreshTokenFn;
  /**
   * Skip the per-request access re-validation in fetchWithRefresh. Set for the
   * initial settings load and for the settings-validation fetch itself (which IS
   * the check) so they don't recurse through the guard.
   */
  skipAccessCheck?: boolean;
}

/**
 * Fetches Studio settings from the nonai-addin-api. Returns the wire payload
 * normalised into the internal shape — the flat `connections[]` becomes
 * status/lookup only (no `authConfig`); per-connector credentials are
 * resolved later by enrichModulesWithCredentials, which binds them onto
 * `modules[*].connectors[*]`.
 */
export async function fetchStudioSettings(
  args: FetchSettingsArgs,
  opts: FetchSettingsOptions = {},
): Promise<StudioSettingsResponse> {
  // /api/{channel}/settings is served by the nonai-addin-api
  // (Aiva.Studio.Api.Addin.Endpoints.Controllers.SettingsController), so
  // route through the addin-api base URL.
  if (!config.addinApiBaseUrl) {
    throw new StudioUnreachableError("Addin API base URL not configured");
  }

  const response = await studioFetch(buildSettingsUrl(args), args.bearer, opts);

  if (!response.ok) {
    throw new StudioBadResponseError(response.status);
  }

  const payload = (await response.json()) as
    | StudioSettingsWireResponse
    | StudioSettingsResponse;
  return normaliseSettings(payload);
}

// No user in the URL: the API reads who is calling from the bearer token.
function buildSettingsUrl(args: FetchSettingsArgs): string {
  return `${config.addinApiBaseUrl}/api/${encodeURIComponent(args.channel)}/settings`;
}

function buildHeaders(bearer?: string): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  return headers;
}

/**
 * GET an addin-api/web-portal endpoint with the bearer attached, routed through
 * `fetchWithRefresh` so an expired token triggers exactly one silent refresh +
 * retry instead of surfacing the raw 401 to the caller. `fetchWithRefresh` owns
 * the `perf.api.call` / `error.api.*` / `error.network` telemetry (tagged
 * "addin" by URL); callers handle the response status and parsing. A network
 * throw is rewrapped in the module's `StudioUnreachableError`.
 */
async function studioFetch(
  url: string,
  bearer: string | undefined,
  opts: FetchSettingsOptions,
): Promise<Response> {
  try {
    return await fetchWithRefresh(
      url,
      { headers: buildHeaders(bearer) },
      opts.refresh,
      opts.fetchImpl ?? fetch,
      { skipAccessCheck: opts.skipAccessCheck },
    );
  } catch (err) {
    throw new StudioUnreachableError(err);
  }
}

/**
 * Wire → internal model. The backend returns connections as
 * `{name, systemName, status}`; this rebuilds the ConnectionWithAuth
 * shape the UI expects (addinKey = systemName, moduleLinks walked from
 * modules[*].connectors). Credentials (`authConfig`, `apiBaseUrl`) are
 * NOT bound here — they live on modules[*].connectors[*] after
 * enrichModulesWithCredentials runs.
 */
function normaliseSettings(
  payload: StudioSettingsWireResponse | StudioSettingsResponse,
): StudioSettingsResponse {
  const data = payload.data;

  if (isWireData(data)) {
    const connections = data.connections.map((wc) =>
      wireToConnection(wc, data.modules),
    );
    const normalised: StudioSettingsData = {
      client: data.client,
      // Default status to "active" on payloads predating the field so older
      // backends aren't treated as revoked. Only an explicit non-active value gates.
      user: { ...data.user, status: (data.user.status ?? "active").toLowerCase() },
      modules: data.modules,
      // `enabled` is absent on backends predating the field. It MUST default to
      // true rather than being left undefined: undefined is falsy, so every
      // skill would render as disabled against an older backend — the exact
      // inverse of the previous behaviour, where the payload only ever carried
      // enabled skills.
      skillGroups: data.skillGroups.map((g) => ({
        ...g,
        skills: g.skills.map((s) => ({ ...s, enabled: s.enabled ?? true })),
      })),
      connections,
      // Absent on payloads predating the flag → treat as "has access" so the
      // add-in keeps working against older backends; only an explicit false gates it.
      hasChannelAccess: data.hasChannelAccess ?? true,
      accessDeniedReason: data.accessDeniedReason ?? null,
      // AIVA-6350: absent on older backends → treat the AI provider as connected
      // so the add-in keeps answering; only an explicit false gates NL queries.
      aiProviderConnected: data.aiProviderConnected ?? true,
      // Fails open so an add-in talking to an addin-api that predates the field
      // is never gated by a tier that backend cannot report.
      reasoningProviderConnected: data.reasoningProviderConnected ?? true,
      // AIVA-9278: same fail-open default for the Microsoft Graph connector.
      m365ConnectorConnected: data.m365ConnectorConnected ?? true,
      // Absent on backends predating the field → project the (already
      // enabled-only) modules list as all-enabled, so Settings > Modules renders
      // exactly what it did before the catalogue existed rather than blanking.
      moduleCatalogue:
        data.moduleCatalogue ?? data.modules.map((m) => ({ ...m, enabled: true })),
      outlookDisclaimer: data.outlookDisclaimer,
      aiNotice: data.aiNotice,
      externalRecipientWarnEnabled: data.externalRecipientWarnEnabled,
    };
    return { ...payload, data: normalised };
  }

  // Already-normalised path — pass through; no longer patch authConfig
  // here. enrichModulesWithCredentials owns auth binding now.
  return { ...payload, data };
}

function isWireData(
  data: StudioSettingsWireData | StudioSettingsData,
): data is StudioSettingsWireData {
  const first = data.connections[0];
  if (!first) return true;
  // Wire shape has `systemName`; normalised ConnectionWithAuth has `addinKey`.
  return "systemName" in first && !("addinKey" in first);
}

/* ── Per-connector credentials (Key Vault) ───────────────────────────── */

export interface FetchCredentialsArgs {
  channel: string;
  systemName: string;
  bearer?: string;
}

/**
 * Wire shape returned by GET /api/{channel}/connectors/{systemName}/credentials
 * (see ConnectorKeysViewModel on the nonai-addin backend). All auth fields are
 * nullable because Key Vault blobs may omit any of them for connectors that
 * use Amplify / API-key auth.
 */
interface ConnectorAuthConfigWire {
  clientId?: string | null;
  hostedUiDomain?: string | null;
  poolId?: string | null;
  region?: string | null;
  apiKey?: string | null;
  identityProvider?: string | null;
  idpIdentifier?: string | null;
  tokenEndpoint?: string | null;
  appClientId?: string | null;
  authorityUrl?: string | null;
  redirectUri?: string | null;
  requiredScopes?: string | null;
}

interface ConnectorKeysWire {
  systemName: string;
  apiBaseUrl?: string | null;
  baseUrl?: string | null;
  authConfig: ConnectorAuthConfigWire;
}

interface ConnectorKeysWireResponse {
  success?: boolean;
  data: ConnectorKeysWire;
  error?: string | null;
  correlationId?: string | null;
}

/**
 * Fetches per-connector credentials from Key Vault via the addin-api. The
 * caller is normally enrichModulesWithCredentials, which fans these out
 * once per unique systemName after the settings endpoint resolves.
 */
export async function fetchConnectorCredentials(
  args: FetchCredentialsArgs,
  opts: FetchSettingsOptions = {},
): Promise<ConnectorKeysWire> {
  if (!config.addinApiBaseUrl) {
    throw new StudioUnreachableError("Addin API base URL not configured");
  }

  const response = await studioFetch(buildCredentialsUrl(args), args.bearer, opts);

  if (!response.ok) {
    throw new StudioBadResponseError(response.status);
  }

  const payload = (await response.json()) as ConnectorKeysWireResponse;
  return payload.data;
}

function buildCredentialsUrl(args: FetchCredentialsArgs): string {
  return (
    `${config.addinApiBaseUrl}/api/${encodeURIComponent(args.channel)}` +
    `/connectors/${encodeURIComponent(args.systemName)}/credentials`
  );
}

export interface EnrichModulesArgs {
  channel: string;
  bearer?: string;
}

/**
 * Walks `modules[*].connectors[*]`, fans out one `/credentials` call per
 * **unique** `systemName` (in parallel), and binds the merged result onto
 * each module-connector occurrence (`authConfig` + `apiBaseUrl`). Failures
 * fall back to the env-driven `getConnectionAuthConfig` so a single
 * missing Key Vault blob doesn't keep the add-in from booting.
 */
export async function enrichModulesWithCredentials(
  modules: StudioModule[],
  args: EnrichModulesArgs,
  opts: FetchSettingsOptions = {},
): Promise<StudioModule[]> {
  if (modules.length === 0) return modules;

  const uniqueSystemNames = Array.from(
    new Set(modules.flatMap((m) => m.connectors.map((c) => c.systemName))),
  );
  if (uniqueSystemNames.length === 0) return modules;

  const settled = await Promise.allSettled(
    uniqueSystemNames.map((systemName) =>
      fetchConnectorCredentials(
        {
          channel: args.channel,
          systemName,
          bearer: args.bearer,
        },
        opts,
      ),
    ),
  );

  const wireBySystem = new Map<string, ConnectorKeysWire>();
  settled.forEach((result, idx) => {
    if (result.status === "fulfilled") {
      const systemName = uniqueSystemNames[idx];
      if (systemName) wireBySystem.set(systemName, result.value);
    }
  });

  return modules.map((m) => ({
    ...m,
    connectors: m.connectors.map((c) =>
      enrichConnector(c, wireBySystem.get(c.systemName)),
    ),
  }));
}

function enrichConnector(
  connector: StudioModuleConnector,
  wire: ConnectorKeysWire | undefined,
): StudioModuleConnector {
  const fallback = getConnectionAuthConfig(connector.systemName);
  const next: StudioModuleConnector = { ...connector };

  if (wire) {
    const merged = mergeAuthConfig(wire.authConfig, fallback);
    if (merged) next.authConfig = merged;
    const resolvedBaseUrl = wire.apiBaseUrl ?? wire.baseUrl;
    if (resolvedBaseUrl) next.apiBaseUrl = resolvedBaseUrl;
  } else if (fallback) {
    next.authConfig = fallback;
  }
  return next;
}

/**
 * Field-pick merge: live Key Vault values win when non-empty; otherwise the
 * env-driven fallback fills in. Returns undefined if neither side supplies
 * the four required fields (clientId, hostedUiDomain, poolId, region).
 */
function mergeAuthConfig(
  wire: ConnectorAuthConfigWire,
  fallback: ConnectionAuthConfig | undefined,
): ConnectionAuthConfig | undefined {
  const pick = (
    fromWire: string | null | undefined,
    fromFallback: string | undefined,
  ): string | undefined => {
    if (fromWire && fromWire.length > 0) return fromWire;
    return fromFallback;
  };

  const clientId = pick(wire.clientId, fallback?.clientId);
  const hostedUiDomain = pick(wire.hostedUiDomain, fallback?.hostedUiDomain);
  const poolId = pick(wire.poolId, fallback?.poolId);
  const region = pick(wire.region, fallback?.region);
  const apiKey = pick(wire.apiKey, fallback?.apiKey);
  const appClientId = pick(wire.appClientId, fallback?.appClientId);

  // Accept the config if any credential signal is present — not just Cognito fields.
  // API-key-only connectors (e.g. myqueue-tasks) and OAuth2 connectors (e.g. onedrive)
  // have no clientId/poolId/region so the old guard would always drop them.
  if (!clientId && !hostedUiDomain && !poolId && !region && !apiKey && !appClientId) {
    return undefined;
  }

  const merged: ConnectionAuthConfig = {
    clientId: clientId ?? "",
    hostedUiDomain: hostedUiDomain ?? "",
    poolId: poolId ?? "",
    region: region ?? "",
  };
  if (apiKey) merged.apiKey = apiKey;
  const identityProvider = pick(wire.identityProvider, fallback?.identityProvider);
  if (identityProvider) merged.identityProvider = identityProvider;
  const idpIdentifier = pick(wire.idpIdentifier, fallback?.idpIdentifier);
  if (idpIdentifier) merged.idpIdentifier = idpIdentifier;
  const tokenEndpoint = pick(wire.tokenEndpoint, fallback?.tokenEndpoint);
  if (tokenEndpoint) merged.tokenEndpoint = tokenEndpoint;
  if (appClientId) merged.appClientId = appClientId;
  const authorityUrl = pick(wire.authorityUrl, fallback?.authorityUrl);
  if (authorityUrl) merged.authorityUrl = authorityUrl;
  const redirectUri = pick(wire.redirectUri, fallback?.redirectUri);
  if (redirectUri) merged.redirectUri = redirectUri;
  const requiredScopes = pick(wire.requiredScopes, fallback?.requiredScopes);
  if (requiredScopes) merged.requiredScopes = requiredScopes;
  return merged;
}

/**
 * Fetches the full OutlookIntegrationSettings for a tenant directly from the
 * WebPortal API. Returns null when no settings row exists yet (404) or when
 * nonAiApiBaseUrl is not configured. Errors on any other non-OK response.
 */
export async function fetchOutlookIntegrationSettings(
  tenantId: string,
  bearer?: string,
  opts: FetchSettingsOptions = {},
): Promise<OutlookIntegrationSettings | null> {
  if (!config.nonAiApiBaseUrl) return null;

  const url = `${config.nonAiApiBaseUrl}/api/outlook-integration-settings/${encodeURIComponent(tenantId)}`;
  const response = await studioFetch(url, bearer, opts);

  if (response.status === 404) return null;
  if (!response.ok) throw new StudioBadResponseError(response.status);

  const payload = (await response.json()) as { data: OutlookIntegrationSettings };
  return payload.data;
}

/**
 * AIVA-7296: records the add-in sign-in against the Studio WebPortal API
 * (`POST /api/me/login`, MeController). The backend stamps
 * `Users.LastLoginAtUtc` and writes the `user.login` audit row — neither of
 * which any other add-in call produces.
 *
 * This matters beyond the audit trail: the retention worker deactivates active
 * users whose `COALESCE(LastLoginAtUtc, CreatedAtUtc)` is older than the
 * configured threshold. Until the add-in reported its sign-ins, an add-in-only
 * user's clock never advanced past the day their row was created, so they were
 * auto-deactivated while using the product daily.
 *
 * Deliberately a bare `fetch` rather than `studioFetch`: this fires during
 * sign-in, before the access guard has a settings baseline, and it must not
 * recurse through `ensureAccess` or open an auth dialog on a 401. Best-effort
 * throughout — a failed record must never block the user getting into the
 * add-in — and `keepalive` lets it outlive a pane that closes right after
 * sign-in. Returns whether the record landed (for tests / callers that care).
 */
export async function recordStudioLogin(
  bearer: string,
  opts: { fetchImpl?: typeof fetch } = {},
): Promise<boolean> {
  if (!config.nonAiApiBaseUrl || !bearer) return false;

  try {
    const response = await (opts.fetchImpl ?? fetch)(`${config.nonAiApiBaseUrl}/api/me/login`, {
      method: "POST",
      headers: buildHeaders(bearer),
      keepalive: true,
    });
    return response.ok;
  } catch {
    // Swallow — sign-in must succeed even when the audit/last-login write can't.
    return false;
  }
}

function wireToConnection(
  wire: StudioWireConnection,
  modules: StudioModule[],
): ConnectionWithAuth {
  const moduleLinks: Array<{ moduleId: string; order: number }> = [];
  modules.forEach((m, idx) => {
    if (m.connectors.some((c) => c.systemName === wire.systemName)) {
      moduleLinks.push({ moduleId: String(m.id), order: idx });
    }
  });
  const status = wire.status?.toLowerCase() === "connected" ? "active" : "inactive";
  return {
    id: `conn-${wire.systemName}`,
    name: wire.name,
    status,
    health: "healthy",
    addinKey: wire.systemName,
    category: "",
    lastHealthCheckAt: null,
    moduleLinks,
  };
}
